import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Table, Button, Alert, Modal, Spinner, Badge, Nav } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import API_URL from '../../config';

function CalculationHistory() {
    const [benfeitorias, setBenfeitorias] = useState([]);
    const [lotes, setLotes] = useState([]);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [activeTab, setActiveTab] = useState('todos'); // 'todos' | 'benfeitorias' | 'lotes' | 'combined'
    const [selectedItems, setSelectedItems] = useState(new Set()); // Set de "benfeitoria-14" | "lote-6"
    const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
    const [deletingSelected, setDeletingSelected] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => { fetchAll(); }, []);
    useEffect(() => { setSelectedItems(new Set()); setSearch(''); }, [activeTab]);

    const selKey = (item) => `${item._type}-${item.id}`;

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [rb, rl] = await Promise.all([
                axios.get(`${API_URL}/calculations`, { withCredentials: true }),
                axios.get(`${API_URL}/api/lote/calculations`, { withCredentials: true }),
            ]);
            setBenfeitorias(rb.data);
            setLotes(rl.data);
            setError('');
        } catch {
            setError('Erro ao carregar histórico.');
        } finally {
            setLoading(false);
        }
    };

    const fmt = (n) => n?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '—';
    const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString('pt-BR') : '—';

    // ── Listas derivadas ────────────────────────────────────────────────────
    const unified = useMemo(() => [
        ...benfeitorias.map(c => ({ ...c, _type: 'benfeitoria', _nome: c.nome_imovel || 'Sem nome', _ts: c.timestamp })),
        ...lotes.map(l => ({ ...l, _type: 'lote', _nome: l.nome_lote || 'Sem nome', _ts: l.timestamp })),
    ].sort((a, b) => (!a._ts ? 1 : !b._ts ? -1 : new Date(b._ts) - new Date(a._ts))),
    [benfeitorias, lotes]);

    // Aba "Benfeitoria+Lote": imovel_ids que aparecem nos dois tipos
    const combinedItems = useMemo(() => {
        const imovelIdsB = new Set(benfeitorias.filter(c => c.imovel_id).map(c => c.imovel_id));
        const imovelIdsL = new Set(lotes.filter(l => l.imovel_id).map(l => l.imovel_id));
        const shared = [...imovelIdsB].filter(id => imovelIdsL.has(id));
        return unified.filter(i => shared.includes(i.imovel_id))
            .sort((a, b) => (a.imovel_id - b.imovel_id) || (a._type === 'benfeitoria' ? -1 : 1));
    }, [unified, benfeitorias, lotes]);

    const displayed = useMemo(() => {
        if (activeTab === 'benfeitorias') return unified.filter(i => i._type === 'benfeitoria');
        if (activeTab === 'lotes')        return unified.filter(i => i._type === 'lote');
        if (activeTab === 'combined')     return combinedItems;
        return unified;
    }, [activeTab, unified, combinedItems]);

    const filteredDisplayed = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return displayed;
        return displayed.filter(i =>
            (i.imovel_codigo || '').toLowerCase().includes(q) ||
            (i._nome || '').toLowerCase().includes(q) ||
            String(i.id).includes(q)
        );
    }, [displayed, search]);

    const toggleSelect = (item) => setSelectedItems(prev => {
        const next = new Set(prev);
        next.has(selKey(item)) ? next.delete(selKey(item)) : next.add(selKey(item));
        return next;
    });
    const toggleSelectAll = () => {
        if (filteredDisplayed.every(i => selectedItems.has(selKey(i)))) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(filteredDisplayed.map(selKey)));
        }
    };
    const allSelected = filteredDisplayed.length > 0 && filteredDisplayed.every(i => selectedItems.has(selKey(i)));
    const someSelected = selectedItems.size > 0;
    const activeSet = someSelected
        ? filteredDisplayed.filter(i => selectedItems.has(selKey(i)))
        : filteredDisplayed;

    // ── PDF individual ──────────────────────────────────────────────────────
    const handlePdfIndividual = async (item) => {
        const url = item._type === 'benfeitoria'
            ? `${API_URL}/calculations/${item.id}/pdf`
            : `${API_URL}/api/lote/calculations/${item.id}/pdf`;
        try {
            const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([res.data]));
            link.download = `${item._type}_${item.id}.pdf`;
            document.body.appendChild(link); link.click(); link.remove();
        } catch {
            setError('Erro ao gerar PDF.');
        }
    };

    // ── PDF exportação contextual (segue aba ativa / seleção) ───────────────
    const handleExportPdf = async () => {
        try {
            setExportingPdf(true);
            let url = `${API_URL}/api/export-unified-pdf`;
            const source = activeSet; // selecionados ou todos exibidos

            if (someSelected || activeTab === 'combined') {
                // Agrupado por imóvel com todos os campos
                const calcIds = source.filter(i => i._type === 'benfeitoria').map(i => i.id).join(',');
                const loteIds = source.filter(i => i._type === 'lote').map(i => i.id).join(',');
                url += `?calc_ids=${calcIds}&lote_ids=${loteIds}`;
            } else if (activeTab === 'benfeitorias') {
                url += '?filter=benfeitorias';
            } else if (activeTab === 'lotes') {
                url += '?filter=lotes';
            } else {
                url += '?filter=all';
            }

            const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
            const ts = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
            const label = someSelected ? 'selecionados' : { todos: 'unificado', benfeitorias: 'benfeitorias', lotes: 'lotes', combined: 'benfeitoria_lote' }[activeTab];
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            link.download = `relatorio_${label}_${ts}.pdf`;
            document.body.appendChild(link); link.click(); link.remove();
            setMessage('PDF exportado com sucesso!');
        } catch {
            setError('Erro ao exportar PDF.');
        } finally {
            setExportingPdf(false);
        }
    };

    // ── Excel contextual (segue aba ativa / seleção) ────────────────────────
    const handleExportXlsx = () => {
        const wb = XLSX.utils.book_new();
        const source = activeSet;
        const dispB = source.filter(i => i._type === 'benfeitoria');
        const dispL = source.filter(i => i._type === 'lote');

        const parseFatores = (l) => {
            try { return l.fatores_json ? JSON.parse(l.fatores_json) : {}; } catch { return {}; }
        };

        const rowEdif = (c) => ({
            'Código': c.imovel_codigo || `#${c.id}`,
            'Nome do Imóvel': c.nome_imovel || '',
            'Benfeitoria': c.benfeitoria_type || '',
            'Padrão': c.property_standard || '',
            'Área (m²)': c.area,
            'Idade Aparente (anos)': c.apparent_age,
            'Estado de Conservação': c.estado_conservacao || '',
            'Descrição Estado': c.descricao_estado || '',
            'R8N': c.r8n,
            'Intervalo': c.intervalo,
            'Vida Referencial (anos)': c.vida_referencial,
            'Valor Residual': c.valor_residual,
            'Idade Referencial (%)': c.idade_referencial,
            'Depreciação Estado (%)': c.depreciacao_d,
            'FOC': c.foc,
            'VUB (R$/m²)': c.vub,
            'Valor Total (R$)': c.valor_total,
            'Data': fmtDate(c.timestamp),
        });

        const rowLote = (l) => {
            const fat = parseFatores(l);
            return {
                'Código': l.imovel_codigo || `#${l.id}`,
                'Nome do Lote': l.nome_lote || '',
                'Tipo': l.tipo || '',
                'Endereço': l.endereco || '',
                'Área Total (m²)': l.area_total_m2,
                'Dedução (%)': l.deducao_percentual,
                'Área Útil (m²)': l.area_util_m2,
                'Valor por m² (R$)': l.valor_por_m2,
                'Ftopo': fat.ftopo ?? '',
                'Fforma': fat.fforma ?? '',
                'Finfra': fat.finfra ?? '',
                'Ffre': fat.ffre ?? '',
                'Fprof': fat.fprof ?? '',
                'Farea': fat.farea ?? '',
                'Encravado': fat.encravado ? 'Sim' : 'Não',
                'Fator Encravado': fat.encravado ? (fat.encravado_fator ?? '') : '',
                'Valor Homogeneizado m² (R$)': l.valor_homogeneizado || '',
                'Valor Total (R$)': l.valor_total,
                'Data': fmtDate(l.timestamp),
            };
        };

        if (activeTab === 'combined' || someSelected) {
            if (dispB.length > 0)
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dispB.map(rowEdif)), 'Edificações');
            if (dispL.length > 0)
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dispL.map(rowLote)), 'Lotes');

            const codigos = [...new Set(displayed.map(i => i.imovel_codigo || `#${i.id}`))];
            const resumo = codigos.map(cod => {
                const b = dispB.filter(i => (i.imovel_codigo || `#${i.id}`) === cod);
                const l = dispL.filter(i => (i.imovel_codigo || `#${i.id}`) === cod);
                const totalEdif = b.reduce((s, i) => s + (i.valor_total || 0), 0);
                const totalLote = l.reduce((s, i) => s + (i.valor_total || 0), 0);
                return {
                    'Código Imóvel': cod,
                    'Nome': (b[0] || l[0])?._nome || '',
                    'Qtd. Edificações': b.length,
                    'Valor Edificações (R$)': totalEdif,
                    'Qtd. Lotes': l.length,
                    'Valor Lotes (R$)': totalLote,
                    'Valor Total Imóvel (R$)': totalEdif + totalLote,
                };
            });
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumo), 'Resumo por Imóvel');
        } else {
            if (dispB.length > 0)
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dispB.map(rowEdif)), 'Edificações');
            if (dispL.length > 0)
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dispL.map(rowLote)), 'Lotes');

            if (activeTab === 'todos') {
                const rows = displayed.map(i => ({
                    'Tipo': i._type === 'benfeitoria' ? 'Edificação' : `Lote (${i.tipo || ''})`,
                    'Código': i.imovel_codigo || `#${i.id}`,
                    'Nome': i._nome,
                    'Valor Total (R$)': i.valor_total,
                    'Data': fmtDate(i._ts),
                }));
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Resumo');
            }
        }

        if (wb.SheetNames.length === 0) return;

        const label = { todos: 'unificado', benfeitorias: 'edificacoes', lotes: 'lotes', combined: 'benfeitoria_lote' }[activeTab];
        XLSX.writeFile(wb, `avaliacoes_${label}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ── Delete ──────────────────────────────────────────────────────────────
    const handleDeleteClick = (item) => {
        setItemToDelete({ _type: item._type, id: item.id, nome: item._nome, valor_total: item.valor_total });
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        const url = itemToDelete._type === 'benfeitoria'
            ? `${API_URL}/calculations/${itemToDelete.id}`
            : `${API_URL}/api/lote/calculations/${itemToDelete.id}`;
        try {
            setDeletingId(itemToDelete.id);
            await axios.delete(url, { withCredentials: true });
            setMessage(`"${itemToDelete.nome}" deletado com sucesso.`);
            setShowDeleteModal(false);
            setItemToDelete(null);
            fetchAll();
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao deletar.');
        } finally {
            setDeletingId(null);
            setShowDeleteModal(false);
        }
    };

    const confirmDeleteSelected = async () => {
        setDeletingSelected(true);
        const toDelete = filteredDisplayed.filter(i => selectedItems.has(selKey(i)));
        let failed = 0;
        await Promise.all(toDelete.map(async (item) => {
            const url = item._type === 'benfeitoria'
                ? `${API_URL}/calculations/${item.id}`
                : `${API_URL}/api/lote/calculations/${item.id}`;
            try { await axios.delete(url, { withCredentials: true }); }
            catch { failed++; }
        }));
        setDeletingSelected(false);
        setShowDeleteSelectedModal(false);
        setSelectedItems(new Set());
        if (failed > 0) setError(`${failed} item(s) não puderam ser deletados.`);
        else setMessage(`${toDelete.length} item(s) deletado(s) com sucesso.`);
        fetchAll();
    };

    // ── Stats da aba ativa ──────────────────────────────────────────────────
    const totalDisp = displayed.reduce((s, i) => s + (i.valor_total || 0), 0);
    const totalB = benfeitorias.reduce((s, c) => s + (c.valor_total || 0), 0);
    const totalL = lotes.reduce((s, l) => s + (l.valor_total || 0), 0);

    if (loading) return (
        <div className="bc-page">
            <div className="bc-loading"><Spinner animation="border" /><span>Carregando histórico...</span></div>
        </div>
    );

    return (
        <div className="bc-page">
            <div className="container" style={{ maxWidth: '1300px' }}>

                {/* Cabeçalho */}
                <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                    <div className="bc-page-header" style={{ marginBottom: 0 }}>
                        <h2>Histórico Unificado de Avaliações</h2>
                        <p>{benfeitorias.length} benfeitoria(s) · {lotes.length} lote(s)/loteamento(s)</p>
                    </div>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        {someSelected && (
                            <>
                                <span style={{ fontSize: '0.82rem', background: '#fef9c3', border: '1px solid #f59e0b', borderRadius: '6px', padding: '0.3rem 0.75rem', color: '#92400e', fontWeight: 600 }}>
                                    {selectedItems.size} selecionado(s)
                                    <button className="btn btn-link btn-sm p-0 ms-2" style={{ color: '#dc2626', fontSize: '0.78rem' }} onClick={() => setSelectedItems(new Set())}>limpar</button>
                                </span>
                                <button
                                    className="btn btn-sm"
                                    style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, padding: '0.35rem 0.85rem' }}
                                    onClick={() => setShowDeleteSelectedModal(true)}>
                                    🗑️ Apagar selecionados ({selectedItems.size})
                                </button>
                            </>
                        )}
                        <button className="btn bc-btn bc-btn-success"
                            onClick={handleExportPdf}
                            disabled={displayed.length === 0 || exportingPdf}>
                            {exportingPdf
                                ? <><Spinner animation="border" size="sm" className="me-2" />Exportando...</>
                                : someSelected ? `📄 PDF (${selectedItems.size})` : '📄 Exportar PDF'}
                        </button>
                        <button className="btn bc-btn"
                            style={{ background: '#217346', color: '#fff', border: 'none' }}
                            onClick={handleExportXlsx}
                            disabled={displayed.length === 0}>
                            {someSelected ? `📊 Excel (${selectedItems.size})` : '📊 Exportar Excel'}
                        </button>
                    </div>
                </div>

                {/* Stats */}
                {unified.length > 0 && (
                    <div className="bc-stats-row">
                        <div className="bc-stat">
                            <div className="s-label">Registros exibidos</div>
                            <div className="s-value">{displayed.length}</div>
                        </div>
                        <div className="bc-stat">
                            <div className="s-label">Benfeitorias (total)</div>
                            <div className="s-value">{benfeitorias.length}</div>
                        </div>
                        <div className="bc-stat">
                            <div className="s-label">Lotes (total)</div>
                            <div className="s-value">{lotes.length}</div>
                        </div>
                        <div className="bc-stat">
                            <div className="s-label">Valor exibido</div>
                            <div className="s-value" style={{ fontSize: '1rem' }}>
                                R$ {totalDisp.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                            </div>
                        </div>
                    </div>
                )}

                {error   && <Alert variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
                {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}

                {/* Abas */}
                <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={k => setActiveTab(k)}>
                    <Nav.Item><Nav.Link eventKey="todos">Todos ({unified.length})</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="benfeitorias">Benfeitorias ({benfeitorias.length})</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="lotes">Lotes ({lotes.length})</Nav.Link></Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="combined">
                            Benfeitoria+Lote ({combinedItems.length})
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {/* Busca */}
                <div className="mb-3" style={{ maxWidth: '340px' }}>
                    <div className="input-group input-group-sm">
                        <span className="input-group-text" style={{ background: '#f8faff', border: '1px solid #d1d9f0' }}>🔍</span>
                        <input
                            type="text"
                            className="form-control bc-input"
                            placeholder="Buscar por código, nome ou ID…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ borderLeft: 'none' }}
                        />
                        {search && (
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setSearch('')} title="Limpar">✕</button>
                        )}
                    </div>
                    {search && (
                        <small className="text-muted ms-1">{filteredDisplayed.length} resultado(s)</small>
                    )}
                </div>

                {activeTab === 'combined' && combinedItems.length === 0 && (
                    <Alert variant="info">
                        Nenhum registro com benfeitoria <strong>e</strong> lote vinculados ao mesmo imóvel.
                        Vincule os cálculos a um imóvel ao salvar para que apareçam aqui.
                    </Alert>
                )}

                {activeTab === 'combined' && combinedItems.length > 0 && (() => {
                    const imovelIds = [...new Set(combinedItems.map(i => i.imovel_id))];
                    return (
                        <div className="mb-3">
                            {imovelIds.map(iid => {
                                const items = combinedItems.filter(i => i.imovel_id === iid);
                                const totalEdif = items.filter(i => i._type === 'benfeitoria').reduce((s, i) => s + (i.valor_total || 0), 0);
                                const totalLote = items.filter(i => i._type === 'lote').reduce((s, i) => s + (i.valor_total || 0), 0);
                                const totalImovel = totalEdif + totalLote;
                                const nome = items[0]?.imovel_nome || items[0]?.imovel_id;
                                const codigo = items[0]?.imovel_codigo || '';
                                return (
                                    <div key={iid} style={{ background: '#f0f4ff', border: '1.5px solid #4a6fa5', borderRadius: '10px', padding: '0.75rem 1.1rem', marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.95rem', minWidth: '140px' }}>
                                            🏠 {codigo && <span className="me-1 text-primary">[{codigo}]</span>}{nome}
                                        </div>
                                        <div className="d-flex gap-3 flex-wrap ms-auto align-items-center">
                                            <span style={{ fontSize: '0.82rem', color: '#555' }}>
                                                Edificação: <strong>R$ {totalEdif.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                                            </span>
                                            <span style={{ fontSize: '0.82rem', color: '#555' }}>
                                                Lote: <strong>R$ {totalLote.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                                            </span>
                                            <span style={{ background: '#1a56db', color: '#fff', borderRadius: '8px', padding: '0.25rem 0.75rem', fontWeight: 700, fontSize: '0.9rem' }}>
                                                Total Imóvel: R$ {totalImovel.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })()}

                {filteredDisplayed.length === 0 && activeTab !== 'combined' ? (
                    <div className="bc-section">
                        <div className="bc-empty">
                            <span className="bc-empty-icon">📋</span>
                            <strong>{search ? 'Nenhum resultado para a busca' : 'Nenhum registro nesta categoria'}</strong>
                        </div>
                    </div>
                ) : filteredDisplayed.length > 0 && (
                    <div className="bc-table-wrap">
                        <Table className="bc-tbl" responsive hover={false}>
                            <thead>
                                <tr>
                                    <th style={{ width: '36px' }}>
                                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                                            title="Selecionar todos" style={{ cursor: 'pointer' }} />
                                    </th>
                                    <th>Tipo</th>
                                    <th>Código</th>
                                    <th>Nome</th>
                                    {activeTab === 'combined' && <th>Imóvel</th>}
                                    <th>Detalhes</th>
                                    <th>Área (m²)</th>
                                    <th>Valor Unitário</th>
                                    <th>Valor Total (R$)</th>
                                    <th>Data</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDisplayed.map((item, idx) => {
                                    // Separador de grupo na aba combined
                                    const isGroupStart = activeTab === 'combined' && (
                                        idx === 0 || filteredDisplayed[idx - 1].imovel_id !== item.imovel_id
                                    );
                                    const groupTotal = activeTab === 'combined' && isGroupStart
                                        ? displayed.filter(i => i.imovel_id === item.imovel_id).reduce((s, i) => s + (i.valor_total || 0), 0)
                                        : 0;
                                    return [
                                        isGroupStart && (
                                            <tr key={`grp-${item.imovel_id}`} style={{ background: '#e8f0fe' }}>
                                                <td colSpan={10} style={{ fontWeight: 700, fontSize: '0.85rem', padding: '4px 12px' }}>
                                                    🏠 Imóvel: {item.imovel_nome || `ID ${item.imovel_id}`}
                                                    <span className="ms-3 text-primary">
                                                        Total: R$ {fmt(groupTotal)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ),
                                        <tr key={`${item._type}-${item.id}`}
                                            style={selectedItems.has(selKey(item)) ? { background: '#fffbeb' } : {}}>
                                            <td>
                                                <input type="checkbox"
                                                    checked={selectedItems.has(selKey(item))}
                                                    onChange={() => toggleSelect(item)}
                                                    style={{ cursor: 'pointer' }} />
                                            </td>
                                            <td>
                                                {item._type === 'benfeitoria'
                                                    ? <Badge bg="info" text="dark">Benfeitoria</Badge>
                                                    : <Badge bg={item.tipo === 'loteamento' ? 'warning' : 'secondary'} text="dark">
                                                        {item.tipo === 'loteamento' ? 'Loteamento' : 'Lote'}
                                                      </Badge>
                                                }
                                            </td>
                                            <td><Badge bg="primary">{item.imovel_codigo || `#${item.id}`}</Badge></td>
                                            <td><strong>{item._nome}</strong></td>
                                            {activeTab === 'combined' && (
                                                <td><small className="text-muted">{item.imovel_nome || '—'}</small></td>
                                            )}
                                            <td>
                                                {item._type === 'benfeitoria'
                                                    ? <small>{item.benfeitoria_type} · {(item.property_standard || '').replace('Padrão ', '')}</small>
                                                    : <small className="text-muted">{item.endereco || '—'}</small>
                                                }
                                            </td>
                                            <td>
                                                {item._type === 'benfeitoria'
                                                    ? item.area?.toFixed(1)
                                                    : <>
                                                        <div>{item.area_total_m2?.toFixed(1)}</div>
                                                        {item.deducao_percentual > 0 && (
                                                            <small className="text-muted">útil: {item.area_util_m2?.toFixed(1)}</small>
                                                        )}
                                                      </>
                                                }
                                            </td>
                                            <td>
                                                {item._type === 'benfeitoria'
                                                    ? <small>VUB: R$ {fmt(item.vub)}/m²</small>
                                                    : <small>
                                                        R$ {fmt(item.valor_por_m2)}/m²
                                                        {item.valor_homogeneizado && (
                                                            <><br /><span className="text-muted">Homog.: R$ {fmt(item.valor_homogeneizado)}/m²</span></>
                                                        )}
                                                      </small>
                                                }
                                            </td>
                                            <td><strong style={{ color: 'var(--primary)' }}>R$ {fmt(item.valor_total)}</strong></td>
                                            <td><small className="text-muted">{fmtDate(item._ts)}</small></td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <button className="btn btn-sm"
                                                        style={{ background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                                                        onClick={() => handlePdfIndividual(item)} title="PDF individual">📄</button>
                                                    <button className="btn btn-sm"
                                                        style={{ background: '#fff0f0', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                                                        onClick={() => handleDeleteClick(item)}
                                                        disabled={deletingId === item.id} title="Deletar">
                                                        {deletingId === item.id ? <Spinner animation="border" size="sm" /> : '🗑️'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ];
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Totais por categoria */}
                {unified.length > 0 && (
                    <div className="d-flex gap-4 mt-3 p-3 bg-light rounded flex-wrap">
                        {benfeitorias.length > 0 && (
                            <span><strong>Benfeitorias:</strong> R$ {totalB.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        )}
                        {lotes.length > 0 && (
                            <span><strong>Lotes:</strong> R$ {totalL.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        )}
                        <span className="fw-bold text-primary">
                            Total Geral: R$ {(totalB + totalL).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                )}
            </div>

            {/* Modal exclusão em lote */}
            <Modal show={showDeleteSelectedModal} onHide={() => !deletingSelected && setShowDeleteSelectedModal(false)}>
                <Modal.Header closeButton={!deletingSelected}>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>Confirmar Exclusão em Lote</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Tem certeza que deseja deletar <strong>{selectedItems.size} item(s)</strong>?</p>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '0.75rem 1rem', fontSize: '0.82rem' }}>
                        {filteredDisplayed.filter(i => selectedItems.has(selKey(i))).map(i => (
                            <div key={selKey(i)} className="d-flex justify-content-between">
                                <span>{i._type === 'benfeitoria' ? '🏗️' : '🗺️'} {i._nome || `ID ${i.id}`}</span>
                                <span className="text-muted ms-2">R$ {fmt(i.valor_total)}</span>
                            </div>
                        ))}
                    </div>
                    <p style={{ marginTop: '0.75rem', color: '#dc2626', fontSize: '0.82rem', marginBottom: 0 }}>Esta ação não pode ser desfeita.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowDeleteSelectedModal(false)} disabled={deletingSelected}>Cancelar</Button>
                    <button className="btn bc-btn bc-btn-danger" onClick={confirmDeleteSelected} disabled={deletingSelected}>
                        {deletingSelected ? <><Spinner animation="border" size="sm" className="me-2" />Deletando...</> : `Deletar ${selectedItems.size} item(s)`}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal exclusão */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: 700 }}>Confirmar Exclusão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Tem certeza que deseja deletar:</p>
                    <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '0.875rem 1rem', fontSize: '0.875rem' }}>
                        <strong>{itemToDelete?.nome}</strong><br />
                        <span style={{ color: 'var(--text-muted)' }}>
                            {itemToDelete?._type === 'benfeitoria' ? 'Benfeitoria' : 'Lote'} · ID {itemToDelete?.id} · R$ {itemToDelete?.valor_total?.toFixed(2)}
                        </span>
                    </div>
                    <p style={{ marginTop: '0.75rem', color: '#dc2626', fontSize: '0.82rem', marginBottom: 0 }}>Esta ação não pode ser desfeita.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
                    <button className="btn bc-btn bc-btn-danger" onClick={confirmDelete} disabled={!!deletingId}>
                        {deletingId ? <><Spinner animation="border" size="sm" className="me-2" />Deletando...</> : 'Deletar'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CalculationHistory;
